import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { X } from 'lucide-react';
import { addOnSummary, customerStatus, displayName, formatDate, formatStorageSize } from './customerFormat';
import { setCustomerLocationPing, fetchCustomers } from '../redux/adminSlice';
import CustomerFleetDevicesPanel from './CustomerFleetDevicesPanel';

// Tab strip (fleet CAN-odometer round, 2026-09-24): splits the drawer's
// existing cards into tabs without changing any card's own JSX/behaviour.
// Order matches the contract exactly.
const DETAIL_TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'modules', label: 'Modules' },
  { key: 'devices', label: 'Devices' },
  { key: 'tracking', label: 'Tracking & data' },
  { key: 'danger', label: 'Danger zone' },
];

// Platform-owned GPS ping cadence options for the "Tracking" card below.
// Mirrors the contract in customers/{cid}.locationPingIntervalMinutes
// (adminDashboardF-setCustomerLocationPing): integer 1..60, or absent/null
// for the platform default (5 minutes). Customers never see or control this.
const PING_INTERVAL_MINUTE_OPTIONS = [1, 2, 5, 10, 15, 30];

// Full-detail panel for one customer, opened from the Customers table (row
// click or the "Open" button). Renders at z-40 - BELOW the existing
// CustomerModulesModal / CustomerDataModal (z-50) - so "Manage modules" and
// "Browse data" open on top of this drawer while it stays mounted
// underneath (its own state, e.g. the account form, is preserved).
const CustomerDetailDrawer = ({
  customer,
  onClose,
  onSave,
  onSendPasswordReset,
  onDelete,
  onOpenModules,
  onOpenData,
  blocked = false,
}) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState({ name: '', ownerName: '', email: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null); // {type:'success'|'error', text}
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [resetError, setResetError] = useState(null);
  const [pingSelection, setPingSelection] = useState('');
  const [isSavingPing, setIsSavingPing] = useState(false);
  const [pingMessage, setPingMessage] = useState(null); // {type:'success'|'error', text}
  const [activeTab, setActiveTab] = useState('overview');
  const savingRef = useRef(false);
  const deletingRef = useRef(false);
  const sendingResetRef = useRef(false);
  const savingPingRef = useRef(false);
  const saveMessageTimerRef = useRef(null);
  const pingMessageTimerRef = useRef(null);

  // Always holds the latest `customer` prop. The sync effect below only
  // wants to re-run when the customer IDENTITY changes (opening a different
  // row), never when redux patches the same customer's fields (e.g. right
  // after this drawer's own successful save) - reading through a ref keeps
  // the effect honestly exhaustive-deps-clean without pulling `customer`
  // itself into the dependency array.
  const customerRef = useRef(customer);
  customerRef.current = customer;

  useEffect(() => {
    const current = customerRef.current;
    setForm({
      name: current?.name || '',
      ownerName: current?.ownerName || '',
      email: current?.email || '',
    });
    setPingSelection(
      typeof current?.locationPingIntervalMinutes === 'number'
        ? String(current.locationPingIntervalMinutes)
        : ''
    );
    setSaveMessage(null);
    setDeleteError(null);
    setResetError(null);
    setPingMessage(null);
    // Active tab resets to Overview when a DIFFERENT customer opens, same
    // identity-change effect the form sync above uses - but stays put while
    // the same customer's row is merely patched by redux (e.g. this
    // drawer's own save), matching every other piece of state reset here.
    setActiveTab('overview');
  }, [customer?.id]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // CustomerModulesModal/CustomerDataModal render above this drawer
      // (z-50 vs z-40) and neither has its own Escape handler - without the
      // `blocked` check, Escape closes THIS drawer out from under an open
      // modal instead of the modal itself.
      if (e.key === 'Escape' && !blocked) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, blocked]);

  useEffect(() => {
    return () => {
      if (saveMessageTimerRef.current) {
        clearTimeout(saveMessageTimerRef.current);
      }
      if (pingMessageTimerRef.current) {
        clearTimeout(pingMessageTimerRef.current);
      }
    };
  }, []);

  if (!customer) {
    return null;
  }

  const status = customerStatus(customer);
  const usage = customer.usageStats;
  const addOns = addOnSummary(customer);

  const storedName = (customer.name || '').trim();
  const storedOwnerName = (customer.ownerName || '').trim();
  const storedEmail = (customer.email || '').trim();

  const dirty =
    form.name.trim() !== storedName ||
    form.ownerName.trim() !== storedOwnerName ||
    form.email.trim() !== storedEmail;

  const storedPingValue =
    typeof customer.locationPingIntervalMinutes === 'number'
      ? String(customer.locationPingIntervalMinutes)
      : '';
  const pingDirty = pingSelection !== storedPingValue;

  const handleFieldChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleCancel = () => {
    setForm({
      name: customer.name || '',
      ownerName: customer.ownerName || '',
      email: customer.email || '',
    });
    setSaveMessage(null);
  };

  const handleSave = async () => {
    if (savingRef.current || !dirty) {
      return; // sync guard: a double-click (or a stale click once Save
      // already resolved and dirty cleared) can never double-submit
    }

    const trimmedName = form.name.trim();
    const trimmedOwnerName = form.ownerName.trim();
    const trimmedEmail = form.email.trim();
    const emailChanged = trimmedEmail !== storedEmail;

    if (emailChanged) {
      const confirmed = window.confirm(
        `Change the sign-in email from ${storedEmail} to ${trimmedEmail}? The owner will sign in with the new address.`
      );
      if (!confirmed) {
        return;
      }
    }

    const payload = {};
    if (trimmedName !== storedName) {
      payload.name = trimmedName;
    }
    if (trimmedOwnerName !== storedOwnerName) {
      payload.ownerName = trimmedOwnerName;
    }
    if (emailChanged) {
      payload.email = trimmedEmail;
    }

    savingRef.current = true;
    setIsSaving(true);
    setSaveMessage(null);
    try {
      await onSave(customer.id, payload);
      if (saveMessageTimerRef.current) {
        clearTimeout(saveMessageTimerRef.current);
      }
      setSaveMessage({ type: 'success', text: 'Saved' });
      saveMessageTimerRef.current = setTimeout(() => {
        setSaveMessage(null);
      }, 2000);
    } catch (err) {
      setSaveMessage({
        type: 'error',
        text: typeof err === 'string' ? err : err?.message || 'Failed to save. Please try again.',
      });
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleSavePing = async () => {
    if (savingPingRef.current || !pingDirty) {
      return; // sync guard: a double-click (or a stale click once Save
      // already resolved and pingDirty cleared) can never double-submit
    }

    savingPingRef.current = true;
    setIsSavingPing(true);
    setPingMessage(null);
    try {
      await dispatch(
        setCustomerLocationPing({
          customerId: customer.id,
          locationPingIntervalMinutes: pingSelection === '' ? null : Number(pingSelection),
        })
      ).unwrap();
      // No optimistic patch on this thunk (see adminSlice.js) - await the
      // refetch so the stored value, the disabled Save button and "Last
      // changed" all catch up together before the success message shows.
      await dispatch(fetchCustomers());
      if (pingMessageTimerRef.current) {
        clearTimeout(pingMessageTimerRef.current);
      }
      setPingMessage({ type: 'success', text: 'Saved' });
      pingMessageTimerRef.current = setTimeout(() => {
        setPingMessage(null);
      }, 2000);
    } catch (err) {
      setPingMessage({
        type: 'error',
        text: typeof err === 'string' ? err : err?.message || 'Failed to save. Please try again.',
      });
    } finally {
      savingPingRef.current = false;
      setIsSavingPing(false);
    }
  };

  const handlePasswordResetClick = async () => {
    if (sendingResetRef.current) {
      return; // sync guard: a double-click can never send two reset emails
    }
    if (!window.confirm(`Are you sure you want to send a password reset email to ${customer.email}?`)) {
      return;
    }
    sendingResetRef.current = true;
    setIsSendingReset(true);
    setResetError(null);
    try {
      await onSendPasswordReset(customer.email);
    } catch (err) {
      setResetError(
        typeof err === 'string' ? err : err?.message || 'Failed to send password reset email. Please try again.'
      );
    } finally {
      sendingResetRef.current = false;
      setIsSendingReset(false);
    }
  };

  const handleDeleteClick = async () => {
    if (deletingRef.current) {
      return; // sync guard: a double-click can never double-submit
    }
    const name = displayName(customer);
    if (
      !window.confirm(
        `Are you sure you want to delete the customer "${name}"? This only removes the customer record from this portal.`
      )
    ) {
      return;
    }
    deletingRef.current = true;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await onDelete(customer.id);
      onClose();
    } catch (err) {
      deletingRef.current = false;
      setIsDeleting(false);
      setDeleteError(typeof err === 'string' ? err : err?.message || 'Failed to delete. Please try again.');
      return;
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 flex w-full max-w-[960px] flex-col bg-[#f4f7fa] shadow-2xl">
        <div className="flex-none bg-[linear-gradient(120deg,#4a5c70,#374a5e_55%,#2a3a4c)] px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="truncate text-lg font-bold text-white">{displayName(customer)}</h3>
              <span
                className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-[rgba(215,226,236,.9)]"
                title={status.hint}
              >
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: status.color }} />
                {status.label}
              </span>
              <p className="mt-1 truncate text-sm text-[rgba(215,226,236,.75)]">{customer.email}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex-none rounded-lg border border-white/25 p-1.5 text-white transition hover:bg-white/10"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-none border-b border-[rgba(150,165,180,.25)] bg-white px-5">
          <div role="tablist" className="flex gap-1 overflow-x-auto">
            {DETAIL_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={
                  activeTab === tab.key
                    ? 'flex-none whitespace-nowrap border-b-2 border-[#2f66b8] px-3 py-3 text-sm font-semibold text-[#2f66b8]'
                    : 'flex-none whitespace-nowrap border-b-2 border-transparent px-3 py-3 text-sm font-semibold text-[#64798a] transition hover:text-[#1d2b33]'
                }
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
        {activeTab === 'overview' && (
          <>
          <div className="rounded-xl border border-[rgba(150,165,180,.25)] bg-white p-6 shadow-[0_1px_3px_rgba(29,43,51,.08)]">
            <h4 className="mb-3 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">Account</h4>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">
                  Company name
                </label>
                <input
                  type="text"
                  required
                  maxLength={120}
                  value={form.name}
                  onChange={handleFieldChange('name')}
                  className="w-full rounded-lg border border-[rgba(150,165,180,.4)] bg-white p-2 text-[#1d2b33] focus:border-[#2f66b8] focus:outline-none focus:ring-2 focus:ring-[#2f66b8]/40"
                />
                {customer.companyName && customer.companyName !== customer.name && (
                  <p className="mt-1 text-xs text-[#64798a]">
                    Shown in the office app as {customer.companyName} — saving here updates both.
                  </p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">
                  Owner&rsquo;s name
                </label>
                <input
                  type="text"
                  maxLength={80}
                  placeholder="Owner's name"
                  value={form.ownerName}
                  onChange={handleFieldChange('ownerName')}
                  className="w-full rounded-lg border border-[rgba(150,165,180,.4)] bg-white p-2 text-[#1d2b33] focus:border-[#2f66b8] focus:outline-none focus:ring-2 focus:ring-[#2f66b8]/40"
                />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">
                  Owner&rsquo;s email
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={handleFieldChange('email')}
                  className="w-full rounded-lg border border-[rgba(150,165,180,.4)] bg-white p-2 text-[#1d2b33] focus:border-[#2f66b8] focus:outline-none focus:ring-2 focus:ring-[#2f66b8]/40"
                />
                <p className="mt-1 text-xs text-[#64798a]">
                  Changing the email also changes the owner&rsquo;s sign-in address.
                </p>
              </div>

              {saveMessage && (
                <p
                  className={
                    saveMessage.type === 'success'
                      ? 'text-sm font-semibold text-[#2ea183]'
                      : 'text-sm font-semibold text-[#d96a5b]'
                  }
                >
                  {saveMessage.text}
                </p>
              )}

              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={handleSave}
                  // Save is a plain button outside a <form>, so the inputs'
                  // `required` attributes are inert - this is the only
                  // guard against sending an empty name/email (the server
                  // still validates too, but the field-empty case shouldn't
                  // round-trip to find that out).
                  disabled={!dirty || isSaving || !form.name.trim() || !form.email.trim()}
                  className="rounded-lg bg-[#2f66b8] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#285aa5] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSaving ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={!dirty || isSaving}
                  className="text-sm font-semibold text-[#2f66b8] hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-[rgba(150,165,180,.25)] bg-white p-6 shadow-[0_1px_3px_rgba(29,43,51,.08)]">
            <h4 className="mb-3 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">Usage</h4>
            {usage ? (
              <>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-[#64798a]">Workers</p>
                    <p className="font-semibold text-[#1d2b33]">{usage.employees ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-[#64798a]">Jobsites</p>
                    <p className="font-semibold text-[#1d2b33]">{usage.jobsites ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-[#64798a]">GC workers</p>
                    <p className="font-semibold text-[#1d2b33]">{usage.gcWorkers ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-[#64798a]">GC companies</p>
                    <p className="font-semibold text-[#1d2b33]">{usage.gcSubCompanies ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-[#64798a]">Storage</p>
                    <p className="font-semibold text-[#1d2b33]">
                      {formatStorageSize(usage.storageBytes || 0)}{' '}
                      <span className="text-xs font-normal text-[#64798a]">
                        ({usage.storageObjects ?? 0} files)
                      </span>
                    </p>
                  </div>
                </div>
                <p className="mt-3 text-xs text-[#64798a]">Counted nightly · last {formatDate(usage.computedAt)}</p>
              </>
            ) : (
              <p className="text-xs text-[#64798a]">Not counted yet (runs nightly at 3:30)</p>
            )}
          </div>

          <div className="rounded-xl border border-[rgba(150,165,180,.25)] bg-white p-6 shadow-[0_1px_3px_rgba(29,43,51,.08)]">
            <h4 className="mb-3 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">Sign-in</h4>
            {resetError && <p className="mb-2 text-sm font-semibold text-[#d96a5b]">{resetError}</p>}
            <button
              type="button"
              onClick={handlePasswordResetClick}
              disabled={isSendingReset}
              className="rounded-lg border border-[rgba(150,165,180,.4)] bg-white px-4 py-2 text-sm font-semibold text-[#1d2b33] transition hover:bg-[#f4f7fa] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSendingReset ? 'Sending…' : 'Send password reset email'}
            </button>
          </div>

          <div className="px-1 text-xs text-[#64798a]">
            <p>Created {formatDate(customer.createdAt)}</p>
            <p className="font-mono">Customer id {customer.id}</p>
          </div>
          </>
        )}

        {activeTab === 'modules' && (
          <>
          <div className="rounded-xl border border-[rgba(150,165,180,.25)] bg-white p-6 shadow-[0_1px_3px_rgba(29,43,51,.08)]">
            <h4 className="mb-3 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">Modules</h4>
            <p className="text-sm text-[#1d2b33]">
              Simple warehouse: {addOns.warehouse ? 'on' : 'off'} · Simple GC: {addOns.gc ? 'on' : 'off'}
            </p>
            <p className="mt-1 text-xs text-[#64798a]">
              {addOns.offCount === 0 ? 'All modules on' : `${addOns.offCount} modules off`}
            </p>
            <button
              type="button"
              onClick={() => onOpenModules(customer)}
              className="mt-3 rounded-lg border border-[rgba(150,165,180,.4)] bg-white px-4 py-2 text-sm font-semibold text-[#1d2b33] transition hover:bg-[#f4f7fa]"
            >
              Manage modules
            </button>
          </div>
          </>
        )}

        {activeTab === 'devices' && (
          <CustomerFleetDevicesPanel customer={customer} />
        )}

        {activeTab === 'tracking' && (
          <>
          <div className="rounded-xl border border-[rgba(150,165,180,.25)] bg-white p-6 shadow-[0_1px_3px_rgba(29,43,51,.08)]">
            <h4 className="mb-3 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">Tracking</h4>
            <p className="text-sm text-[#64798a]">
              How often a worker&rsquo;s phone saves a GPS point while travelling between jobsites. Shorter = more
              map detail, more database writes and battery. Customers cannot change this.
            </p>
            <div className="mt-3">
              <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">
                Save a location point every
              </label>
              <select
                value={pingSelection}
                onChange={(e) => setPingSelection(e.target.value)}
                className="w-full max-w-[220px] rounded-lg border border-[rgba(150,165,180,.4)] bg-white p-2 text-[#1d2b33] focus:border-[#2f66b8] focus:outline-none focus:ring-2 focus:ring-[#2f66b8]/40"
              >
                <option value="">Platform default (5 minutes)</option>
                {PING_INTERVAL_MINUTE_OPTIONS.map((minutes) => (
                  <option key={minutes} value={String(minutes)}>
                    {minutes} minute{minutes === 1 ? '' : 's'}
                  </option>
                ))}
              </select>
            </div>

            {pingMessage && (
              <p
                className={
                  pingMessage.type === 'success'
                    ? 'mt-2 text-sm font-semibold text-[#2ea183]'
                    : 'mt-2 text-sm font-semibold text-[#d96a5b]'
                }
              >
                {pingMessage.text}
              </p>
            )}

            <button
              type="button"
              onClick={handleSavePing}
              disabled={!pingDirty || isSavingPing}
              className="mt-3 rounded-lg bg-[#2f66b8] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#285aa5] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSavingPing ? 'Saving…' : 'Save'}
            </button>

            {customer.locationPingUpdatedAt && (
              <p className="mt-3 text-xs text-[#64798a]">Last changed {formatDate(customer.locationPingUpdatedAt)}</p>
            )}
          </div>

          <div className="rounded-xl border border-[rgba(150,165,180,.25)] bg-white p-6 shadow-[0_1px_3px_rgba(29,43,51,.08)]">
            <h4 className="mb-3 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">Data</h4>
            <p className="text-sm text-[#64798a]">Read-only preview of this customer&rsquo;s records.</p>
            <button
              type="button"
              onClick={() => onOpenData(customer)}
              className="mt-3 rounded-lg border border-[rgba(150,165,180,.4)] bg-white px-4 py-2 text-sm font-semibold text-[#1d2b33] transition hover:bg-[#f4f7fa]"
            >
              Browse data
            </button>
          </div>
          </>
        )}

        {activeTab === 'danger' && (
          <>
          <div className="rounded-xl border border-[rgba(150,165,180,.25)] border-l-4 border-l-[#d96a5b] bg-white p-6 shadow-[0_1px_3px_rgba(29,43,51,.08)]">
            <h4 className="mb-3 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">Danger zone</h4>
            <p className="text-sm text-[#64798a]">
              Delete removes the customer record from this portal only. The owner&rsquo;s sign-in and the
              company&rsquo;s data are not removed.
            </p>
            {deleteError && <p className="mt-2 text-sm font-semibold text-[#d96a5b]">{deleteError}</p>}
            <button
              type="button"
              onClick={handleDeleteClick}
              disabled={isDeleting}
              className="mt-3 rounded-lg bg-[#d96a5b] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#c85a4b] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isDeleting ? 'Deleting…' : 'Delete customer'}
            </button>
          </div>
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDetailDrawer;
